import { QuoteCartProvider } from '@/components/quotes/QuoteCartContext'
import { QuotesPageSkeleton } from '@/components/quotes/QuotesPageSkeleton'
import { Card, CardContent } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useFillApplicationState } from '../../../../models/fillApplication'
import { type InsuranceApplication, useInsuranceApplication } from '../../../../models/insuranceApplication'
import { type Org, showsQuotesPage, useOrganisation, useOrgApplication } from '../../../../models/user'
import { InsuranceApplicationView } from '../../InsuranceApplication'
import { ContractorAnswersCard } from './ContractorAnswersCard'

interface QuoteTabProps {
  orgId: string
  /** Renders the customer's Quotes Page; left off, staff get the application chat transcript. */
  quotesPage?: boolean
}

/** What the customer sees on /quotes, nothing more; staff-only notes go on the Insurance tab. */
export function QuoteTab({ orgId, quotesPage }: QuoteTabProps) {
  const { application, isLoading } = useOrgApplication(orgId)
  const { organisation } = useOrganisation(orgId)

  if (isLoading) {
    return quotesPage ? (
      <div className="mt-8">
        <QuotesPageSkeleton />
      </div>
    ) : (
      <SkeletonRows className="mt-8" rows={6} />
    )
  }

  if (!application) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-sm">No application found for this organization.</p>
        </CardContent>
      </Card>
    )
  }

  // Keyed: the customers split view keeps this tab mounted as staff move between customers,
  // so the embedded view would otherwise carry the previous one's state over.
  return (
    <EmbeddedApplication
      key={application.id}
      orgId={orgId}
      application={application}
      organisation={organisation}
      quotesPage={quotesPage}
    />
  )
}

function EmbeddedApplication({
  orgId,
  application,
  organisation,
  quotesPage,
}: {
  orgId: string
  application: InsuranceApplication
  organisation?: Org
  quotesPage?: boolean
}) {
  // The embedded view polls this query; reading it swaps to quotes the moment carriers are
  // asked, which the org-application snapshot above would miss until a reload.
  const { data: polled } = useInsuranceApplication({ id: application.id, enabled: false })

  const lastCreatedUserId = organisation?.users
    ? [...organisation.users].sort((a, b) => b.dateCreated.localeCompare(a.dateCreated))[0]?.id
    : undefined

  const showsQuotes = quotesPage && showsQuotesPage(organisation, polled ?? application)
  // Checkout is refused until the customer confirms their information, so the cart stays closed.
  const { state: fillState } = useFillApplicationState(quotesPage ? orgId : undefined)
  const awaitingConfirmation = !!showsQuotes && !!fillState?.needsReviewAttestation
  // From the polled application, so answers given while this tab is open show up.
  const answers = quotesPage ? [] : ((polled ?? application).contractorAnswers ?? [])

  return (
    <QuoteCartProvider applicationId={application.id}>
      <div className="mt-8">
        {answers.length > 0 && <ContractorAnswersCard answers={answers} />}
        {/* showCartButton: neither staff layout's nav has a cart, so the summary row carries the opener. */}
        <InsuranceApplicationView
          applicationId={application.id}
          userId={lastCreatedUserId}
          org={organisation}
          quotesPage={showsQuotes}
          showCartButton={!awaitingConfirmation}
          staffView
        />
      </div>
    </QuoteCartProvider>
  )
}
